/**
 * currency — the ONE conversion + formatting boundary for prices (#64).
 *
 * Prices reach this module from two places. Cardmarket data — everything the
 * app came with — is euros. The price provider we pay for (JustTCG) quotes
 * dollars. Each figure is stored and computed in the currency it was quoted in
 * and travels with that code; this module only changes how it is *displayed*.
 * Original amounts are never mutated and never re-saved in another currency.
 *
 * Display currencies are exactly Auto / ILS / EUR / USD (approved decision —
 * do not extend the list). "Auto" resolves from the device's locale/timezone
 * region, never from precise location.
 *
 * Conversion rules:
 *  - A price is converted only NUMERICALLY, with a valid rate from an
 *    approved source. Swapping the symbol without converting the number is
 *    exactly the bug this module exists to kill.
 *  - A converted figure is an estimate and is always marked "≈".
 *  - No trustworthy rate → the figure is shown in its OWN currency instead.
 *    Honest euros beat fabricated shekels, and an honest dollar beats both.
 *  - Two amounts in different currencies are listed, never added — see
 *    formatTotals. A total that mixes currencies is worse than no total.
 *  - Rounding happens at display time only.
 *
 * RATES ARE CURRENTLY BLOCKED: no approved/licensed FX-rate source is
 * configured (owner input — provider choice, licence and credential).
 * getActiveRates() returns null until one is wired, so every currency
 * selection today falls back to showing each figure in the currency it was
 * quoted in. When a source exists, implement it behind getActiveRates() and
 * nothing else changes.
 *
 * THE GOVERNING INVARIANT: an amount never travels without its currency.
 * Cardmarket data is EUR and the price provider quotes USD, so "the currency"
 * is a property of each figure, not of the app. Every function here that
 * returns or renders money either carries the code alongside the number or
 * takes it as an explicit argument, and nothing adds two amounts without their
 * codes being equal — see formatTotals, which JOINS rather than sums.
 */

/** Currency of the embedded Cardmarket price data. */
export const SOURCE_CURRENCY = "EUR";

/** The only selectable preferences. "auto" resolves via resolveDisplayCurrency. */
export const DISPLAY_CURRENCY_OPTIONS = ["auto", "ILS", "EUR", "USD"];

/**
 * Concrete currencies a price can render in.
 *
 * Exported because it is the ceiling on every OTHER currency list in the app,
 * not this module's private business. `formatPrice` below silently falls back
 * to SOURCE_CURRENCY for a code that is not here, so any list that lets an
 * unsupported code reach a formatter produces a relabelled figure rather than
 * an error — the #64 bug, arriving through the back door. A list that decides
 * which quoted currencies may reach a price surface must therefore be checked
 * against this one; utils/priceAdapter.js does that at module load.
 */
export const SUPPORTED_DISPLAY_CURRENCIES = Object.freeze(["ILS", "EUR", "USD"]);

// Local alias so every use below reads the exported array itself. Two literals
// would be two things to keep in step, and the export would stop describing
// what the formatter actually accepts the first time one of them moved.
const SUPPORTED = SUPPORTED_DISPLAY_CURRENCIES;

// Euro-area ISO regions for Auto resolution. Anything not listed here and not
// IL/US falls back to the source currency — showing EUR to a region we cannot
// place is honest; guessing is not.
const EURO_AREA = new Set([
  "AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR",
  "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK",
]);

const deviceEnv = () => {
  let locale = "";
  let timeZone = "";
  try {
    locale =
      (typeof navigator !== "undefined" && navigator.language) ||
      Intl.DateTimeFormat().resolvedOptions().locale ||
      "";
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (_) {
    // No Intl / no navigator — resolution falls through to the source currency.
  }
  return { locale, timeZone };
};

/** Region subtag of a BCP-47 locale ("he-IL" → "IL"), or "". */
const localeRegion = (locale) => {
  const match = /[-_]([A-Za-z]{2})(?:$|[-_])/.exec(String(locale || ""));
  return match ? match[1].toUpperCase() : "";
};

/**
 * Concrete display currency for a stored preference.
 * Explicit ILS/EUR/USD wins; "auto" (and anything unrecognised) resolves from
 * the device region — Israel→ILS, US→USD, euro area→EUR, unknown→source.
 * @param {string} preference — one of DISPLAY_CURRENCY_OPTIONS (or garbage)
 * @param {{locale?: string, timeZone?: string}} [env] — injectable for tests
 * @returns {"ILS"|"EUR"|"USD"}
 */
export const resolveDisplayCurrency = (preference, env) => {
  if (SUPPORTED.includes(preference)) return preference;

  const { locale, timeZone } = env || deviceEnv();
  const region = localeRegion(locale);

  if (region === "IL" || timeZone === "Asia/Jerusalem") return "ILS";
  if (region === "US") return "USD";
  if (EURO_AREA.has(region)) return "EUR";
  return SOURCE_CURRENCY;
};

/**
 * FX rates from an approved source, or null.
 *
 * BLOCKED — intentionally returns null. There is no approved/licensed
 * exchange-rate provider configured for this app (owner decision: provider,
 * licence, credential). Rates must never be guessed, hardcoded or scraped.
 * When a source is approved, return
 *   { base: "EUR", rates: { ILS: <n>, USD: <n> }, fetchedAt: <ISO string> }
 * from it (cached copies must keep their real fetchedAt so staleness stays
 * visible), and conversion turns on everywhere with no other change.
 * @returns {null|{base: string, rates: Object<string, number>, fetchedAt: string}}
 */
export const getActiveRates = () => null;

/**
 * A finite numeric amount, or null.
 *
 * `Number(null)` and `Number("")` are both 0, so a missing price would coerce
 * into a confident "€0.00" — telling a collector a card is worth nothing when
 * we simply do not know. Absent is not zero, so those cases are rejected here
 * and every surface renders nothing instead. Numeric strings still pass: some
 * API fields arrive that way.
 * @returns {number|null}
 */
const toAmount = (amount) => {
  if (amount === null || amount === undefined || amount === "") return null;
  const value = Number(amount);
  return Number.isFinite(value) ? value : null;
};

/**
 * Numeric conversion, or null when it cannot be done honestly.
 * Identity when target is the amount's OWN currency; otherwise requires a
 * finite positive rate quoted against that same currency.
 *
 * `fromCurrency` exists because not every figure in the app is a Cardmarket
 * euro any more: the price provider quotes USD. Reading the identity check off
 * the SOURCE_CURRENCY constant would have called a USD amount "already
 * converted" the moment EUR was requested, handing back the number unchanged
 * under a euro sign.
 * @returns {number|null}
 */
export const convertAmount = (
  amount,
  toCurrency,
  rates,
  fromCurrency = SOURCE_CURRENCY
) => {
  const value = toAmount(amount);
  if (value === null) return null;
  if (toCurrency === fromCurrency) return value;

  if (!rates || rates.base !== fromCurrency) return null;
  const rate = rates.rates && rates.rates[toCurrency];
  if (typeof rate !== "number" || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }
  return value * rate;
};

/**
 * "€12.50" / "$12.50" / "₪48.75" — symbol, separators and 2-digit precision
 * from Intl. The locale is pinned so output does not vary by device.
 */
export const formatMoney = (amount, currency) => {
  const value = toAmount(amount);
  if (value === null) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  }).format(value);
};

/**
 * [B-22] What the user typed into a money field, as a number, or null when it
 * is not a plain amount. The ONE parser for every money input (the asking price
 * and the trade cash field), so two fields can never read "12,50" two ways.
 *
 * Samsung's number pad offers both "." and ",", and in Israel and most of
 * Europe the comma IS the decimal point, so a single comma followed by one or
 * two digits is read as one: "12,50" is 12.5. Anything else is refused rather
 * than guessed: "1,250" means 1.25 in one country and 1250 in another, and
 * `parseFloat` read "12,50" as 12 without a word.
 */
export const parseMoneyInput = (text) => {
  const raw = String(text ?? "").trim().replace(/^(\d+),(\d\d?)$/, "$1.$2");
  return /^\d+(\.\d\d?)?$/.test(raw) ? Number(raw) : null;
};

/**
 * The bare symbol for a currency ("€", "$", "₪").
 *
 * For labelling an INPUT, where the user is typing a number that will be
 * stored: that number is always in the source currency, so the symbol beside
 * it must be the source symbol, never the display one. Showing "₪" over a
 * field whose value is saved and compared as euros is the same lie this module
 * exists to prevent, just pointed the other way.
 */
export const currencySymbol = (currency = SOURCE_CURRENCY) => {
  const part = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  })
    .formatToParts(0)
    .find((p) => p.type === "currency");
  return part ? part.value : currency;
};

/**
 * How an amount should be displayed.
 *
 * `options.amountCurrency` is what the figure IS, not what the reader wants:
 * a Cardmarket estimate is EUR, a provider quote is USD. It defaults to
 * SOURCE_CURRENCY so the ~30 call sites that hand over a Cardmarket figure and
 * nothing else keep rendering exactly as before.
 *
 * That fallback also RELABELS, and this note used to claim the opposite —
 * "every amount renders in its own currency". It does not: an `amountCurrency`
 * outside SUPPORTED_DISPLAY_CURRENCIES is DISCARDED, and the number is printed
 * with the source symbol instead. A figure handed in as GBP comes back wearing
 * a euro sign, which is #64 exactly. That is the CURRENT behaviour, not a
 * behaviour anyone has argued is correct; it is merely harmless while nothing
 * can hand this function a code it cannot format. What keeps that true is
 * utils/priceAdapter.js binding its allow-list — the one list that decides
 * which quoted currencies reach a price surface — to this module's SUPPORTED
 * set at module load. So: with rates null (today), a SUPPORTED amount renders
 * in its own currency, unconverted and unmarked, which is the honest result
 * rather than a hardcoded euro one.
 *
 * @param {number} amount — never mutated
 * @param {{displayCurrency?: string, amountCurrency?: string, rates?: object}} [options]
 * @returns {{text: string, currency: string, isConverted: boolean,
 *            sourceText: string}}
 *   text        what to render ("€12.50", or "≈ ₪48.75" when converted)
 *   currency    the currency `text` is denominated in
 *   isConverted true only when a real numeric conversion happened
 *   sourceText  the amount's own-currency rendering, for transparency surfaces
 */
export const formatPrice = (amount, options = {}) => {
  const amountCurrency = SUPPORTED.includes(options.amountCurrency)
    ? options.amountCurrency
    : SOURCE_CURRENCY;
  const sourceText = formatMoney(amount, amountCurrency);
  const target = SUPPORTED.includes(options.displayCurrency)
    ? options.displayCurrency
    : SOURCE_CURRENCY;

  if (target !== amountCurrency) {
    const converted = convertAmount(amount, target, options.rates, amountCurrency);
    if (converted != null) {
      // "≈" because a converted figure is an estimate by definition; rounding
      // is applied here, at display, never to the stored amount.
      return {
        text: `≈ ${formatMoney(converted, target)}`,
        currency: target,
        isConverted: true,
        sourceText,
      };
    }
  }

  // Own currency requested, or no trustworthy rate: show the real figure.
  return {
    text: sourceText,
    currency: amountCurrency,
    isConverted: false,
    sourceText,
  };
};

/**
 * Several per-currency subtotals, side by side: "€412.30 + $4.20".
 *
 * The "+" is a SEPARATOR, never an operator. There is no approved FX source, so
 * a collection holding a euro-priced card and a dollar-priced one has no single
 * total — and printing one number would be the "one dollar, not one euro" lie
 * in aggregate form. Listing both is the honest shape.
 *
 * It lives here rather than beside its callers because honestCopy.test.js
 * forbids a hardcoded currency symbol outside this module, and every symbol in
 * mixed-currency copy has to come from `currencySymbol()`/Intl below.
 *
 * @param {Object<string, number>} byCurrency — subtotal per ISO code
 * @param {string[]} currencies — render order
 * @param {(amount: number, currency: string) => string} [formatAmount]
 */
export const formatTotals = (byCurrency, currencies, formatAmount) => {
  const render =
    typeof formatAmount === "function"
      ? formatAmount
      : (value, currency) => formatMoney(value, currency);
  const codes =
    currencies && currencies.length
      ? currencies
      : Object.keys(byCurrency || {}).sort();
  return codes
    .map((code) => render(byCurrency?.[code], code))
    .filter((text) => typeof text === "string" && text !== "")
    .join(" + ");
};

/**
 * The sentence a surface has to say when it is holding figures in two
 * currencies and cannot compare them.
 *
 * Assembled here, not at the call site, for the same reason as formatTotals:
 * the symbols must come from Intl. Returns null below two currencies — there is
 * nothing incomparable about a single one.
 */
export const mixedCurrencyNote = (currencies = []) => {
  const symbols = (currencies || []).filter(Boolean).map((c) => currencySymbol(c));
  if (symbols.length < 2) return null;
  const last = symbols[symbols.length - 1];
  const rest = symbols.slice(0, -1).join(", ");
  return (
    `Some cards are priced in ${rest} and some in ${last} — we can’t compare ` +
    "those without an exchange rate. Both sides are listed above."
  );
};

/**
 * What to say about a cash term attached to a side whose OWN cards are priced
 * in more than one currency.
 *
 * This is a different failure from the one cashOffer.js's `cashFairnessNote`
 * describes, and saying the wrong one is worse than saying nothing. That note
 * names a conversion — "we can’t convert X to Y yet" — which presumes the side
 * has a single currency the cash would have to be converted INTO. A side
 * holding both a euro-priced and a dollar-priced card has no such target at
 * all: it cannot be totalled in the first place, so there is nothing for the
 * cash to be converted to, and no exchange rate would change that. Attaching
 * €50 to such a side used to print "we can’t convert EUR to EUR yet", because
 * the missing basis defaulted to the source currency on its way into that
 * sentence.
 *
 * Assembled here rather than at the call site for the same reason as
 * `formatTotals` and `mixedCurrencyNote`: the symbols must come from Intl
 * (honestCopy.test.js). Returns null when there is no cash, and null below two
 * currencies — one currency is a basis, and the conversion note is the honest
 * sentence there.
 *
 * @param {{amount: number, currency: string}|null} cash
 * @param {string[]} currencies — the side's own card currencies
 * @returns {string|null}
 */
export const mixedSideCashNote = (cash, currencies = []) => {
  if (!cash) return null;
  const symbols = (currencies || []).filter(Boolean).map((c) => currencySymbol(c));
  if (symbols.length < 2) return null;
  const last = symbols[symbols.length - 1];
  const rest = symbols.slice(0, -1).join(", ");
  return (
    `${formatMoney(cash.amount, cash.currency)} isn’t included in the fairness ` +
    `estimate — your cards are priced in ${rest} and ${last}, so this side has ` +
    "no single total to add it to. The agreed amount is unchanged."
  );
};

/** "· updated 7 Oct 2022", or "" when the stamp is missing or unparseable. */
const updatedSuffix = (raw) => {
  if (!raw) return "";
  // Cardmarket's updatedAt is a "YYYY/MM/DD" string; tolerate ISO too.
  const parsed = new Date(String(raw).replace(/\//g, "-"));
  if (Number.isNaN(parsed.getTime())) return "";
  const date = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
  return ` · updated ${date}`;
};

/**
 * Provenance line for the price a surface is ACTUALLY SHOWING: where that
 * figure comes from, what currency it is in, and how old it is — "Cardmarket
 * price (EUR) · updated 7 Oct 2022". The age is the honest counterweight to any
 * impression of live data; show it wherever a price influences a decision.
 *
 * `shown` is the object getConditionPrice returned for the figure on screen. It
 * is required whenever the card can be provider-priced: a `$4.20` sitting under
 * a line reading "Cardmarket price (EUR)" is a new instance of exactly the bug
 * #64 exists to prevent, on the one surface the UI marks "not an estimate".
 * Omitted, this describes the Cardmarket block as it always did.
 *
 * @param {object} card — card document
 * @param {{source?: string, currency?: string}} [shown] — from getConditionPrice
 * @returns {string|null} null when there is no data to attribute
 */
export const priceProvenance = (card, shown = null) => {
  if (shown?.source === "sales") {
    const cp = card?.conditionPrices;
    if (cp) {
      const provider = cp.source || "Price provider";
      const currency = shown.currency || cp.currency || "";
      return `${provider} price (${currency})${updatedSuffix(cp.updatedAt)}`;
    }
  }

  const cm = card?.cardmarket;
  if (!cm) return null;
  return `Cardmarket price (${SOURCE_CURRENCY})${updatedSuffix(cm.updatedAt)}`;
};

/**
 * Freshness line for a rates object — "Rates updated 3 Jan 2026" — or null
 * when there are no rates (nothing to claim). Cached rates keep their real
 * fetchedAt, so this stays honest about staleness automatically.
 * @returns {string|null}
 */
export const ratesFreshness = (rates) => {
  if (!rates || !rates.fetchedAt) return null;
  const parsed = new Date(rates.fetchedAt);
  if (Number.isNaN(parsed.getTime())) return null;
  const date = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
  return `Rates updated ${date}`;
};
